import { avgKeys, clamp } from '../../LocalEmotionUtils';
import type { EmotionLabel } from '../../types';
import type { EmotionScore } from './Scorer';
import type { EyeReliabilityContext } from './EyeContextAnalyzer';

// ── Artifact detection thresholds ──
export const ARTIFACT_TH = {
  sadFrownStrong: 0.14,
  sadFrownMedium: 0.12,
  sadFrownSubtle: 0.055,
  sadFrownArtifactCeil: 0.08,
  sadBrowSubtle: 0.10,
  sadShrugStrong: 0.42,
  sadShrugProfile: 0.35,
  sadShrugArtifactCeil: 0.30,
  sadShrugSupport: 0.28,
  sadBrowInnerSupport: 0.08,
  sadLookDownSupport: 0.08,
  lipCurveSadStrong: 0.065,
  lipCurveSadWeak: 0.03,
  lipCurveSadVeryWeak: 0.02,
  mouthOpenMarCeil: 0.44,
  mouthOpenLipTensionCeil: 0.20,
  yawnLikeScoreFloor: 0.35,
  yawnLikeEyeWideCeil: 0.20,
  yawnLikeMarFloor: 0.24,
  yawnLikeMouthStretchFloor: 0.18,
  yawnLikeMouthLowerDownFloor: 0.28,
  yawnLikeMouthFunnelFloor: 0.18,
  postYawnScoreCeil: 0.12,
  postYawnFrownCeil: 0.06,
  postYawnBrowDownFloor: 0.16,
  postYawnSquintFloor: 0.12,
  occludedReliabilityCeil: 0.35,
  occludedFrownCeil: 0.07,
  occludedShrugLow: 0.30,
  occludedShrugHigh: 0.55,
  occludedJawOpenCeil: 0.18,
  occludedMarCeil: 0.22,
  occludedFrownTight: 0.03,
  occludedPressCeil: 0.20,
  occludedRollCeil: 0.20,
  occludedStretchCeil: 0.18,
  noGeomYawnScoreFloor: 0.45,
  noGeomJawOpenFloor: 0.24,
  noGeomEyeWideCeil: 0.30,
  noGeomFrownCeil: 0.12,
  noGeomLipCurveCeil: 0.10,
  noGeomEmergingScoreFloor: 0.42,
  noGeomEmergingJawFloor: 0.30,
  noGeomEmergingEyeWideCeil: 0.33,
  noGeomEmergingFrownCeil: 0.15,
  lowLightSeverityFloor: 0.3,
  noReliableGeometryMarCeil: 0.01,
} as const;

// ── Mobile pattern thresholds ──
export const MOBILE_TH = {
  focusSquint: 0.10,
  focusSquintLowLight: 0.06,
  focusBrowDownCeil: 0.22,
  focusBrowDownCeilLowLight: 0.26,
  focusJawOpenCeil: 0.15,
  focusSmileCeil: 0.10,
  focusFrownCeil: 0.08,
  focusLipTensionCeil: 0.12,
  focusJawForwardCeil: 0.10,
  angrySquintFloor: 0.15,
  angryBrowDownFloor: 0.22,
  focusFramesRequired: 3,
  focusFramesRequiredLowLight: 4,
  angryFramesRequired: 3,
  yawnFramesRequired: 3,
  yawnFramesRequiredLowLight: 2,
  stabilityMultiplier: 4,
  stabilityMultiplierLowLight: 6,
  stabilityFloor: 0.015,
  focusPromotionScoreCeil: 0.60,
} as const;

export const MIN_EMOTION_SCORE_DEFAULT = 0.15;
export const NEUTRAL_WINNER: EmotionScore = { emotion: 'neutral', score: 0, confidence: 0.9, valence: 0, arousal: 0.1 };

// ── Frame signal extraction ──

export interface FrameSignals {
  isLowLight: boolean;
  frown: number;
  browDown: number;
  squintVal: number;
  smileVal: number;
  browInnerUp: number;
  eyeLookDown: number;
  eyeWide: number;
  mouthPress: number;
  mouthRoll: number;
  mouthShrugLower: number;
  mouthLowerDown: number;
  mouthFunnel: number;
  mouthStretch: number;
  jawOpenVal: number;
  jawForwardVal: number;
  lipTension: number;
  pitchExcess: number;
  pitchDownExcess: number;
  lipCurveSadPoseAdjusted: number;
  poseAdjustedEyeLookDown: number;
  poseAdjustedBrowInnerUp: number;
}

export function computeFrameSignals(
  blendshapes: Record<string, number>,
  rawBlendshapes: Record<string, number>,
  lipCurveSad: number,
  eyeContext: EyeReliabilityContext,
): FrameSignals {
  const isLowLight = eyeContext.lowLightSeverity > ARTIFACT_TH.lowLightSeverityFloor;
  const frown = avgKeys(blendshapes, ['mouthFrownLeft', 'mouthFrownRight']);
  const browDown = avgKeys(blendshapes, ['browDownLeft', 'browDownRight']);
  const squintVal = avgKeys(blendshapes, ['eyeSquintLeft', 'eyeSquintRight']);
  const smileVal = avgKeys(blendshapes, ['mouthSmileLeft', 'mouthSmileRight']);
  const browInnerUp = avgKeys(blendshapes, ['browInnerUp']);
  const eyeLookDown = avgKeys(blendshapes, ['eyeLookDownLeft', 'eyeLookDownRight']);
  const eyeWide = avgKeys(blendshapes, ['eyeWideLeft', 'eyeWideRight']);
  const mouthPress = avgKeys(blendshapes, ['mouthPressLeft', 'mouthPressRight']);
  const mouthRoll = avgKeys(blendshapes, ['mouthRollLower']);
  const mouthShrugLower = avgKeys(blendshapes, ['mouthShrugLower']);
  const mouthLowerDown = avgKeys(blendshapes, ['mouthLowerDownLeft', 'mouthLowerDownRight']);
  const mouthFunnel = avgKeys(blendshapes, ['mouthFunnel']);
  const mouthStretch = avgKeys(blendshapes, ['mouthStretchLeft', 'mouthStretchRight']);
  const jawOpenVal = avgKeys(blendshapes, ['jawOpen']);
  const jawForwardVal = blendshapes['jawForward'] ?? 0;
  const lipTension = Math.max(mouthPress, mouthRoll);
  const headPitch = (rawBlendshapes as any)._headPitch ?? 0;
  const pitchUp = Math.max(0, headPitch);
  const pitchDown = Math.max(0, -headPitch);
  const pitchExcess = Math.max(0, pitchUp - 0.06);
  const pitchDownExcess = Math.max(0, pitchDown - 0.04);
  const lipCurveSadPoseAdjusted = Math.max(0, lipCurveSad - Math.min(0.045, pitchExcess * 0.35));
  const poseAdjustedEyeLookDown = Math.max(0, eyeLookDown - Math.min(0.08, pitchExcess * 0.60) - Math.min(0.20, pitchDownExcess * 0.95));
  const poseAdjustedBrowInnerUp = Math.max(0, browInnerUp - Math.min(0.05, pitchExcess * 0.45));
  return {
    isLowLight, frown, browDown, squintVal, smileVal, browInnerUp,
    eyeLookDown, eyeWide, mouthPress, mouthRoll, mouthShrugLower,
    mouthLowerDown, mouthFunnel, mouthStretch, jawOpenVal, jawForwardVal, lipTension,
    pitchExcess, pitchDownExcess, lipCurveSadPoseAdjusted,
    poseAdjustedEyeLookDown, poseAdjustedBrowInnerUp,
  };
}

// ── Artifact detection ──

export interface DetectArtifactsInput {
  sig: FrameSignals;
  yawnDetected: boolean;
  yawnScore: number;
  mar: number;
}

export interface ArtifactFlags {
  strongTrueSadMouth: boolean;
  mouthOpenShrugArtifact: boolean;
  headOnlySadArtifact: boolean;
  yawnLikeSadArtifact: boolean;
  squintPressYawnProfile: boolean;
  noGeomYawnHold: number;
  durationNoGeometryYawn: boolean;
  strongNoGeometryYawn: boolean;
  emergingNoGeometryYawn: boolean;
  effectiveYawnDetected: boolean;
}

export type UpdateStreakFn = (key: string, matched: boolean, opts?: { decay?: number; cap?: number; shouldDecay?: boolean }) => number;

export function detectArtifacts(input: DetectArtifactsInput, updateStreak: UpdateStreakFn): ArtifactFlags {
  const { sig, yawnDetected, yawnScore, mar } = input;
  const {
    isLowLight, frown, squintVal, smileVal, browInnerUp,
    eyeWide, mouthPress, mouthShrugLower, mouthLowerDown,
    mouthFunnel, mouthStretch, jawOpenVal, lipTension,
    pitchExcess, lipCurveSadPoseAdjusted,
  } = sig;

  const strongTrueSadMouth = frown > ARTIFACT_TH.sadFrownStrong
    || lipCurveSadPoseAdjusted > ARTIFACT_TH.lipCurveSadStrong
    || (mouthShrugLower > ARTIFACT_TH.sadShrugStrong && (frown > ARTIFACT_TH.sadFrownSubtle || lipCurveSadPoseAdjusted > ARTIFACT_TH.lipCurveSadWeak));
  const mouthOpenShrugArtifact = mar > ARTIFACT_TH.mouthOpenMarCeil
    && mouthShrugLower > ARTIFACT_TH.sadShrugProfile
    && frown < ARTIFACT_TH.sadFrownSubtle
    && lipCurveSadPoseAdjusted < ARTIFACT_TH.lipCurveSadWeak
    && lipTension < ARTIFACT_TH.mouthOpenLipTensionCeil;
  const headOnlySadArtifact = pitchExcess > 0
    && !strongTrueSadMouth
    && frown < ARTIFACT_TH.sadFrownArtifactCeil
    && mouthShrugLower < ARTIFACT_TH.sadShrugArtifactCeil
    && smileVal < 0.12;
  const yawnLikeSadArtifact = yawnScore >= ARTIFACT_TH.yawnLikeScoreFloor
    && !strongTrueSadMouth
    && frown < ARTIFACT_TH.sadFrownArtifactCeil
    && lipCurveSadPoseAdjusted < ARTIFACT_TH.lipCurveSadWeak
    && eyeWide < ARTIFACT_TH.yawnLikeEyeWideCeil
    && (
      mar > ARTIFACT_TH.yawnLikeMarFloor
      || mouthStretch > ARTIFACT_TH.yawnLikeMouthStretchFloor
      || mouthLowerDown > ARTIFACT_TH.yawnLikeMouthLowerDownFloor
      || mouthFunnel > ARTIFACT_TH.yawnLikeMouthFunnelFloor
    );
  const noReliableMouthGeometry = mar <= ARTIFACT_TH.noReliableGeometryMarCeil;

  const NO_GEOM_YAWN_HOLD_LIMIT = isLowLight ? 3 : 4;
  const lowLightSquintFloor = isLowLight ? 0.12 : 0.20;
  const squintPressYawnProfile = noReliableMouthGeometry
    && squintVal >= lowLightSquintFloor
    && mouthPress >= 0.25
    && browInnerUp < 0.20
    && !strongTrueSadMouth;
  const noGeomYawnHold = updateStreak('noGeomYawn', squintPressYawnProfile, {
    decay: 1, cap: NO_GEOM_YAWN_HOLD_LIMIT + 2, shouldDecay: noReliableMouthGeometry,
  });
  const durationNoGeometryYawn = noReliableMouthGeometry && noGeomYawnHold >= NO_GEOM_YAWN_HOLD_LIMIT;

  const strongNoGeometryYawn = noReliableMouthGeometry
    && !durationNoGeometryYawn
    && yawnScore >= ARTIFACT_TH.noGeomYawnScoreFloor
    && jawOpenVal >= ARTIFACT_TH.noGeomJawOpenFloor
    && squintVal >= (isLowLight ? 0.14 : 0.22)
    && eyeWide < ARTIFACT_TH.noGeomEyeWideCeil
    && frown < ARTIFACT_TH.noGeomFrownCeil
    && lipCurveSadPoseAdjusted < ARTIFACT_TH.noGeomLipCurveCeil
    && !strongTrueSadMouth;
  const emergingNoGeometryYawn = noReliableMouthGeometry
    && !strongNoGeometryYawn
    && !durationNoGeometryYawn
    && yawnScore >= ARTIFACT_TH.noGeomEmergingScoreFloor
    && jawOpenVal >= ARTIFACT_TH.noGeomEmergingJawFloor
    && squintVal >= (isLowLight ? 0.18 : 0.30)
    && eyeWide < ARTIFACT_TH.noGeomEmergingEyeWideCeil
    && frown < ARTIFACT_TH.noGeomEmergingFrownCeil
    && !strongTrueSadMouth;
  const effectiveYawnDetected = yawnDetected || strongNoGeometryYawn || durationNoGeometryYawn;

  return {
    strongTrueSadMouth, mouthOpenShrugArtifact, headOnlySadArtifact, yawnLikeSadArtifact,
    squintPressYawnProfile, noGeomYawnHold, durationNoGeometryYawn,
    strongNoGeometryYawn, emergingNoGeometryYawn, effectiveYawnDetected,
  };
}

// ── Winner selection ──

export interface SelectWinnerInput {
  scores: EmotionScore[];
  sadNeutralExcursion: number;
  minScoreDeltas?: Partial<Record<'sad' | 'angry' | 'happy' | 'surprise', number>>;
  emotionThresholds?: { minScoreByEmotion?: Record<string, number> };
  headOnlySadArtifact: boolean;
  mouthOpenShrugArtifact: boolean;
  strongTrueSadMouth: boolean;
  yawnLikeSadArtifact: boolean;
  postYawnCooldownFrames: number;
  yawnScore: number;
  frown: number;
  browDown: number;
  squintVal: number;
  eyeContext: EyeReliabilityContext;
  lipCurveSadPoseAdjusted: number;
  mouthShrugLower: number;
  jawOpenVal: number;
  mar: number;
  mouthPress: number;
  mouthRoll: number;
  mouthStretch: number;
  emergingNoGeometryYawn: boolean;
  squintPressYawnProfile: boolean;
  noGeomYawnHold: number;
  durationNoGeometryYawn: boolean;
  strongNoGeometryYawn: boolean;
  poseAdjustedEyeLookDown: number;
  poseAdjustedBrowInnerUp: number;
  pitchExcess: number;
  pitchDownExcess: number;
  eyeWide: number;
}

export interface SelectWinnerResult {
  winner: EmotionScore;
  winnerReason: string;
  forceNeutralArtifactReset: boolean;
}

export interface ApplyMobileStreaksInput {
  winner: EmotionScore;
  sig: FrameSignals;
  stab: number;
  baselineStability: number;
}

export function applyMobileStreaks(
  input: ApplyMobileStreaksInput,
  updateStreak: UpdateStreakFn,
): EmotionScore {
  const { sig, stab, baselineStability } = input;
  let winner = input.winner;

  const { isLowLight, squintVal: squintZC, browDown: browDownZC, frown: frownZC,
    jawOpenVal: jawOpenZC, smileVal: smileZC, lipTension: lipTensionZC,
    jawForwardVal: jawForwardZC } = sig;

  const focusSquintTh = isLowLight ? MOBILE_TH.focusSquintLowLight : MOBILE_TH.focusSquint;
  const focusBrowDownCeil = isLowLight ? MOBILE_TH.focusBrowDownCeilLowLight : MOBILE_TH.focusBrowDownCeil;
  const isFocusedPattern = squintZC > focusSquintTh
    && browDownZC < focusBrowDownCeil
    && jawOpenZC < MOBILE_TH.focusJawOpenCeil && smileZC < MOBILE_TH.focusSmileCeil && frownZC < MOBILE_TH.focusFrownCeil
    && lipTensionZC < MOBILE_TH.focusLipTensionCeil && jawForwardZC < MOBILE_TH.focusJawForwardCeil;

  const isAngryPattern = squintZC > MOBILE_TH.angrySquintFloor
    && browDownZC > MOBILE_TH.angryBrowDownFloor;

  const isYawnPattern = winner.emotion === 'yawn';

  const focusStreak = updateStreak('focused', isFocusedPattern);
  const angryStreak = updateStreak('angry', isAngryPattern);
  const yawnStreak = updateStreak('yawn', isYawnPattern, { decay: 1 });

  const stabilityMultiplier = isLowLight ? MOBILE_TH.stabilityMultiplierLowLight : MOBILE_TH.stabilityMultiplier;
  const isStable = stab < 0 || stab <= Math.max(baselineStability * stabilityMultiplier, MOBILE_TH.stabilityFloor);
  const FOCUS_FRAMES_REQUIRED = isLowLight ? MOBILE_TH.focusFramesRequiredLowLight : MOBILE_TH.focusFramesRequired;
  const ANGRY_FRAMES_REQUIRED = MOBILE_TH.angryFramesRequired;
  const YAWN_FRAMES_REQUIRED = isLowLight ? MOBILE_TH.yawnFramesRequiredLowLight : MOBILE_TH.yawnFramesRequired;

  if ((winner.emotion === 'sad' || winner.emotion === 'neutral' || winner.emotion === 'angry') &&
      winner.score < MOBILE_TH.focusPromotionScoreCeil &&
      focusStreak >= FOCUS_FRAMES_REQUIRED &&
      isStable &&
      !isAngryPattern) {
    winner = { emotion: 'focused', score: 0.35, confidence: 0.80, valence: 0.1, arousal: 0.4 };
  }

  if (winner.emotion === 'angry' && angryStreak < ANGRY_FRAMES_REQUIRED) {
    winner = { emotion: 'neutral', score: 0, confidence: 0.9, valence: 0, arousal: 0.1 };
  }

  if (winner.emotion === 'yawn' && yawnStreak < YAWN_FRAMES_REQUIRED) {
    winner = { emotion: 'neutral', score: 0, confidence: 0.9, valence: 0, arousal: 0.1 };
  }

  return winner;
}

export function selectWinner(input: SelectWinnerInput, _vlog: (...args: any[]) => void): SelectWinnerResult {
  const {
    scores, sadNeutralExcursion, minScoreDeltas, emotionThresholds,
    headOnlySadArtifact, mouthOpenShrugArtifact, strongTrueSadMouth, yawnLikeSadArtifact,
    postYawnCooldownFrames, yawnScore, frown, browDown, squintVal,
    eyeContext, lipCurveSadPoseAdjusted, mouthShrugLower, jawOpenVal, mar,
    mouthPress, mouthRoll, mouthStretch,
    emergingNoGeometryYawn, squintPressYawnProfile, noGeomYawnHold, durationNoGeometryYawn, strongNoGeometryYawn,
    poseAdjustedEyeLookDown, poseAdjustedBrowInnerUp, pitchExcess, pitchDownExcess, eyeWide,
  } = input;

  let forceNeutralArtifactReset = false;

  const sadMinScore = clamp(0.12 + sadNeutralExcursion * 0.20 + (minScoreDeltas?.sad ?? 0), 0.10, 0.22);
  const MIN_SCORE_BY_EMOTION: Partial<Record<EmotionLabel, number>> = {
    sad: sadMinScore,
    angry: clamp(0.22 + (minScoreDeltas?.angry ?? 0), 0.18, 0.30),
    happy: clamp(0.12 + (minScoreDeltas?.happy ?? 0), 0.08, 0.20),
    surprise: clamp(0.14 + (minScoreDeltas?.surprise ?? 0), 0.10, 0.22),
    ...(emotionThresholds?.minScoreByEmotion ?? {}),
  };

  let winner: EmotionScore = { emotion: 'neutral', score: 0, confidence: 0.5, valence: 0, arousal: 0.1 };
  let winnerReason = 'neutral (default)';

  for (const s of scores) {
    const minForEmotion = MIN_SCORE_BY_EMOTION[s.emotion] ?? MIN_EMOTION_SCORE_DEFAULT;
    if (s.score > winner.score && s.score >= minForEmotion) {
      winner = s;
      winnerReason = `selected ${s.emotion} (score=${s.score.toFixed(3)} >= min=${minForEmotion.toFixed(2)})`;
    }
  }

  // ── Artifact guards ──
  if (winner.emotion === 'sad' && headOnlySadArtifact && !strongTrueSadMouth) {
    winner = { ...NEUTRAL_WINNER };
    winnerReason = 'sad suppressed by pitch-only artifact guard';
  }
  if (winner.emotion === 'sad' && mouthOpenShrugArtifact && !strongTrueSadMouth) {
    winner = { ...NEUTRAL_WINNER };
    _vlog(`[EIE][DEBUG] poseAdjEyeLookDown=${poseAdjustedEyeLookDown} poseAdjBrowInnerUp=${poseAdjustedBrowInnerUp} pitchExcess=${pitchExcess} pitchDownExcess=${pitchDownExcess} eyeWide=${eyeWide} mouthStretch=${mouthStretch} sadFrownMediumTh=${ARTIFACT_TH.sadFrownMedium} sadBrowSubtleTh=${ARTIFACT_TH.sadBrowSubtle} sadShrugSupportTh=${ARTIFACT_TH.sadShrugSupport} sadBrowInnerSupportTh=${ARTIFACT_TH.sadBrowInnerSupport} sadLookDownSupportTh=${ARTIFACT_TH.sadLookDownSupport} winnerReason=${winnerReason}`);
    winnerReason = 'sad suppressed by open-mouth shrug artifact guard';
  }
  if (winner.emotion === 'sad' && yawnLikeSadArtifact) {
    winner = { ...NEUTRAL_WINNER };
    winnerReason = 'sad suppressed by yawn-like mouth artifact guard';
  }
  const postYawnSadTailArtifact = postYawnCooldownFrames > 0
    && winner.emotion === 'sad'
    && !strongTrueSadMouth
    && yawnScore <= ARTIFACT_TH.postYawnScoreCeil
    && frown < ARTIFACT_TH.postYawnFrownCeil
    && browDown > ARTIFACT_TH.postYawnBrowDownFloor
    && squintVal > ARTIFACT_TH.postYawnSquintFloor;
  if (postYawnSadTailArtifact) {
    winner = { ...NEUTRAL_WINNER };
    winnerReason = 'sad suppressed by post-yawn brow/squint tail artifact guard';
  }
  const occludedLowReliabilitySadArtifact = winner.emotion === 'sad'
    && eyeContext.eyeOccluded
    && eyeContext.eyeReliability <= ARTIFACT_TH.occludedReliabilityCeil
    && !strongTrueSadMouth
    && yawnScore <= ARTIFACT_TH.postYawnScoreCeil
    && frown < ARTIFACT_TH.occludedFrownCeil
    && lipCurveSadPoseAdjusted < ARTIFACT_TH.lipCurveSadWeak
    && (
      mouthShrugLower < ARTIFACT_TH.occludedShrugLow
      || (
        mouthShrugLower > ARTIFACT_TH.occludedShrugHigh
        && jawOpenVal < ARTIFACT_TH.occludedJawOpenCeil
        && mar < ARTIFACT_TH.occludedMarCeil
        && frown < ARTIFACT_TH.occludedFrownTight
        && lipCurveSadPoseAdjusted < ARTIFACT_TH.lipCurveSadVeryWeak
      )
    )
    && mouthPress < ARTIFACT_TH.occludedPressCeil
    && mouthRoll < ARTIFACT_TH.occludedRollCeil
    && mouthStretch < ARTIFACT_TH.occludedStretchCeil;
  if (occludedLowReliabilitySadArtifact) {
    winner = { ...NEUTRAL_WINNER };
    winnerReason = 'sad suppressed by low-reliability eye-occlusion artifact guard';
    forceNeutralArtifactReset = true;
  }

  // ── No-geometry yawn guards & promotions ──
  if (emergingNoGeometryYawn && (winner.emotion === 'angry' || winner.emotion === 'happy' || winner.emotion === 'surprise')) {
    winner = { emotion: 'neutral', score: 0, confidence: 0.88, valence: 0, arousal: 0.1 };
    winnerReason = 'suppressed pre-yawn no-geometry flicker';
  }
  if (squintPressYawnProfile && noGeomYawnHold >= 2 && !durationNoGeometryYawn
      && (winner.emotion === 'angry' || winner.emotion === 'happy' || winner.emotion === 'sad' || winner.emotion === 'disgust' || winner.emotion === 'surprise')) {
    winner = { emotion: 'neutral', score: 0, confidence: 0.88, valence: 0, arousal: 0.1 };
    winnerReason = 'suppressed pre-yawn hold accumulation flicker';
  }
  if (strongNoGeometryYawn && winner.emotion !== 'yawn') {
    winner = { emotion: 'yawn', score: yawnScore, confidence: Math.max(0.84, yawnScore), valence: 0, arousal: 0.12 };
    winnerReason = 'promoted strong no-geometry yawn fallback';
  }
  if (durationNoGeometryYawn && winner.emotion !== 'yawn') {
    winner = { emotion: 'yawn', score: Math.max(0.70, yawnScore), confidence: 0.88, valence: 0, arousal: 0.12 };
    winnerReason = 'promoted duration no-geometry yawn (desktop-style hold)';
  }

  return { winner, winnerReason, forceNeutralArtifactReset };
}

// ── Persistence state machine ──

export interface PersistenceInput {
  winner: EmotionScore;
  forceNeutralArtifactReset: boolean;
  pendingEmotion: string;
  pendingEmotionCount: number;
  committedEmotion: string;
  committedEmotionHold: number;
  postEmotionSettlingFrames: number;
  isMobileRuntime?: boolean;
  eyeContext: EyeReliabilityContext;
  yawnScore: number;
  mar: number;
  emotionThresholds?: { strongInstantScoreByEmotion?: Record<string, number> };
  adaptiveConfig?: {
    persistenceOverrides?: { sadFrames?: number; angryExtraFrames?: number; committedHoldFrames?: number };
    strongInstantScoreDeltas?: Partial<Record<'sad' | 'angry' | 'happy' | 'surprise', number>>;
  };
  config: { persistenceFrames: number };
}

export interface PersistenceResult {
  winner: EmotionScore;
  pendingEmotion: string;
  pendingEmotionCount: number;
  committedEmotion: string;
  committedEmotionHold: number;
  postEmotionSettlingFrames: number;
}

export function applyPersistence(input: PersistenceInput): PersistenceResult {
  let {
    winner, forceNeutralArtifactReset,
    pendingEmotion, pendingEmotionCount,
    committedEmotion, committedEmotionHold,
    postEmotionSettlingFrames,
    isMobileRuntime, eyeContext, yawnScore, mar,
    emotionThresholds, adaptiveConfig, config,
  } = input;

  const persistenceOverrides = adaptiveConfig?.persistenceOverrides;
  const persistRequired = isMobileRuntime
    ? Math.max(4, (config.persistenceFrames ?? 3) + 1)
    : (config.persistenceFrames ?? 3) + 1;
  const strongInstantScoreDeltas = adaptiveConfig?.strongInstantScoreDeltas;

  if (winner.emotion !== 'neutral' && winner.emotion !== 'focused') {
    if (winner.emotion === pendingEmotion) {
      pendingEmotionCount = Math.min(pendingEmotionCount + 1, persistRequired);
    } else {
      pendingEmotion = winner.emotion;
      pendingEmotionCount = 1;
    }
    const customStrongInstantScore = emotionThresholds?.strongInstantScoreByEmotion?.[winner.emotion];
    const strongInstantEmotion = isMobileRuntime
      ? false
      : (
        (typeof customStrongInstantScore === 'number' && winner.score >= customStrongInstantScore) ||
        (winner.emotion === 'sad' && winner.score >= clamp(0.18 + (strongInstantScoreDeltas?.sad ?? 0), 0.14, 0.24)) ||
        (winner.emotion === 'angry' && winner.score >= clamp(0.26 + (strongInstantScoreDeltas?.angry ?? 0), 0.20, 0.34)) ||
        (winner.emotion === 'happy' && winner.score >= clamp(0.16 + (strongInstantScoreDeltas?.happy ?? 0), 0.12, 0.22)) ||
        (winner.emotion === 'surprise' && winner.score >= clamp(0.28 + (strongInstantScoreDeltas?.surprise ?? 0), 0.22, 0.36)) ||
        winner.emotion === committedEmotion
      );
    const glassesAngryExtraFrames = winner.emotion === 'angry' && eyeContext.eyeOccluded
      ? (persistenceOverrides?.angryExtraFrames ?? 1)
      : 0;
    const sadFrames = clamp(persistenceOverrides?.sadFrames ?? Math.min(2, persistRequired), 1, Math.max(1, persistRequired));
    const framesNeeded = (winner.emotion === 'yawn' || strongInstantEmotion)
      ? 1
      : isMobileRuntime
        ? (winner.emotion === 'surprise' ? 3 : persistRequired)
        : (winner.emotion === 'sad' ? sadFrames : persistRequired + glassesAngryExtraFrames);
    if (pendingEmotionCount < framesNeeded) {
      winner = { emotion: 'neutral', score: 0, confidence: 0.9, valence: 0, arousal: 0.1 };
    }
  } else {
    pendingEmotion = winner.emotion;
    pendingEmotionCount = 0;
  }

  if (forceNeutralArtifactReset) {
    pendingEmotion = 'neutral';
    pendingEmotionCount = 0;
    committedEmotion = 'neutral';
    committedEmotionHold = 0;
  }

  if (postEmotionSettlingFrames > 0) {
    postEmotionSettlingFrames--;
    pendingEmotion = 'neutral';
    pendingEmotionCount = 0;
  }

  if (committedEmotionHold > 0) {
    committedEmotionHold--;
    if (winner.emotion === 'neutral' && committedEmotion !== 'neutral' && committedEmotion !== 'focused') {
      if (committedEmotion === 'yawn') {
        const lowLightYawnUncertain = eyeContext.eyeOccluded && eyeContext.lowLightSeverity >= 0.1;
        const yawnStillStrong = yawnScore >= 0.4 || mar >= 0.30;
        if (lowLightYawnUncertain && yawnStillStrong) {
          winner = { emotion: 'yawn', score: 0, confidence: 0.86, valence: 0, arousal: 0.1 };
          committedEmotion = 'yawn';
          committedEmotionHold = Math.max(committedEmotionHold, 1);
        } else if (isMobileRuntime) {
          winner = { emotion: 'yawn', score: 0, confidence: 0.82, valence: 0, arousal: 0.1 };
        } else {
          committedEmotion = 'neutral';
          committedEmotionHold = 0;
          postEmotionSettlingFrames = 3;
        }
      } else {
        winner = { emotion: committedEmotion as EmotionLabel, score: 0, confidence: 0.88, valence: 0, arousal: 0.1 };
      }
    }
    if (winner.emotion !== 'neutral' && winner.emotion !== 'focused' && winner.emotion !== committedEmotion) {
      const strongSadTakeover = committedEmotion === 'angry' && winner.emotion === 'sad' && winner.score >= 0.11;
      if (strongSadTakeover) {
        committedEmotion = 'sad';
        committedEmotionHold = Math.max(2, Math.floor(8 / 2));
      } else if (winner.emotion === 'yawn') {
        committedEmotion = 'yawn';
        committedEmotionHold = 0;
      } else {
        winner = { emotion: committedEmotion as EmotionLabel, score: 0, confidence: 0.9, valence: 0, arousal: 0.1 };
      }
    }
  } else if (winner.emotion !== 'neutral' && winner.emotion !== 'focused') {
    if (winner.emotion !== committedEmotion) {
      committedEmotion = winner.emotion;
      const holdFrames = isMobileRuntime && winner.emotion === 'yawn'
        ? 18
        : clamp(persistenceOverrides?.committedHoldFrames ?? 8, 4, 12);
      committedEmotionHold = holdFrames;
    }
  }

  return { winner, pendingEmotion, pendingEmotionCount, committedEmotion, committedEmotionHold, postEmotionSettlingFrames };
}

// ── Smoothed output ──

export interface ComputeSmoothedOutputInput {
  winner: EmotionScore;
  smoothed: import('./EmotionFrameProcessor').SmoothedState | null;
  smoothingFactor: number;
  duchenneFactor: number | undefined;
  rawBlendshapes: Record<string, number>;
  handNearFace: boolean;
  handNearMouth: boolean;
  yawnScore: number;
  effectiveYawnDetected: boolean;
  eyeContext: EyeReliabilityContext;
}

export interface ComputeSmoothedOutputResult {
  smoothed: import('./EmotionFrameProcessor').SmoothedState;
  emotionalState: Record<string, any>;
}

export function computeSmoothedOutput(input: ComputeSmoothedOutputInput): ComputeSmoothedOutputResult {
  const {
    winner, smoothingFactor, duchenneFactor,
    rawBlendshapes, handNearFace, handNearMouth,
    yawnScore, effectiveYawnDetected, eyeContext,
  } = input;
  let smoothed = input.smoothed;

  const anchor = {
    valence: typeof winner.valence === 'number' ? winner.valence : 0,
    arousal: typeof winner.arousal === 'number' ? winner.arousal : 0.1,
  };
  const intensity = clamp(winner.score / 0.6, 0, 1);
  const valence = anchor.valence * intensity;
  const arousal = anchor.arousal * intensity + 0.1 * (1 - intensity);

  if (smoothed) {
    smoothed = {
      emotion: winner.emotion,
      valence: smoothed.valence * (1 - smoothingFactor) + valence * smoothingFactor,
      arousal: smoothed.arousal * (1 - smoothingFactor) + arousal * smoothingFactor,
      confidence: smoothed.confidence * (1 - smoothingFactor) + winner.confidence * smoothingFactor,
    };
  } else {
    smoothed = { emotion: winner.emotion, valence, arousal, confidence: winner.confidence };
  }

  const emotionalState = {
    emotion: smoothed.emotion,
    valence: Math.round(smoothed.valence * 100) / 100,
    arousal: Math.round(smoothed.arousal * 100) / 100,
    confidence: Math.round(smoothed.confidence * 100) / 100,
    authenticity: duchenneFactor,
    timestamp: Date.now(),
    blendshapes: rawBlendshapes,
    handNearFace: !!handNearFace,
    handNearMouth: !!handNearMouth,
    yawn: Math.round(yawnScore * 100) / 100,
    yawnScore: Math.round(yawnScore * 100) / 100,
    yawnDetected: effectiveYawnDetected,
    eyeReliability: Math.round(eyeContext.eyeReliability * 100) / 100,
    eyeOccluded: eyeContext.eyeOccluded,
  };

  return { smoothed, emotionalState };
}
